/*
 * file-preview 富预览卡片
 *
 * 用于对话窗口里展示表格类产物（xlsx）的内联预览：
 *   文件名 + 预览模式标签 + 统计行(X 条记录·Y 列·大小)
 *   + 内联表格(列可点跳转) + 「预览」(全屏) + 「立即下载」
 *
 * 内联小表和全屏预览都来自 preview_url 指向的裁剪 xlsx。
 * 没有 preview_url 的文件仍走 FileListBlock 的普通导出卡。
 *
 * 全屏「预览」复用 DocumentPreview（只读：不传选区/编辑回调），source 用裁剪后的 preview_url。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import IconVipCrown from '../../../components/IconVipCrown'
import { Modal } from 'antd'
import ExcelJS from 'exceljs'
import JSZip from 'jszip'
import { DocumentPreview } from '../DocumentPreview'
import { downloadFile } from '../attachments/download'
import { loadFileBuffer } from '../../hooks/useFileLoader'
import { FileItem, FilePreviewCell } from './types'
import './FilePreviewCard.scss'

/** aiClerk 文件卡片全屏预览的 xlsx 缩放;AI 问照片(fitPreviewToWindow)用 zoom=1 让字体更大 */
const PREVIEW_ZOOM = 0.7
const DRAWING_NS =
  'http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing'
const MAIN_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main'
const IMAGE_FORMULA_RE = /^\s*(?:_xlfn\.)?IMAGE\s*\(\s*["']([^"']+)["']/i
const DISPIMG_FORMULA_RE =
  /^\s*(?:_xlfn\.)?(?:_xlfn\.|_xlws\.)*DISPIMG\s*\(\s*["']([^"']+)["']/i

interface FilePreviewCardProps {
  /** 文件数据，需含 preview_url；其余字段含义见 FileItem */
  file: FileItem
  /**
   * 全屏预览是否按预览窗口宽度铺开列宽(一屏约 8 列、少则铺满、多则横向滚动)。
   * 仅 AI 问照片传 true;aiClerk 的文件卡片不传 → 保持文件原始列宽(行为不变)。
   */
  fitPreviewToWindow?: boolean
  /**
   * 非会员锁定:在操作区(预览 / 立即下载)盖一层会员专属蒙层,点击走 onBeforeDownload
   * 跳会员页。仅 AI 问照片传 true;aiClerk 文件卡片不传 → 不锁,行为不变。
   */
  downloadLocked?: boolean
  /**
   * 下载 / 预览前拦截:返回 false 阻止本次操作(并由调用方负责跳会员页等)。
   * 不传 → 不拦截(行为不变)。
   */
  onBeforeDownload?: (file: FileItem) => boolean
  /**
   * 「预览 / 立即下载」按钮点击通知(埋点用), 在拦截判定前触发、不影响行为。
   * 仅 AI 问照片传(转 ai_b8112_click 点击卡片项); aiClerk 不传 → 行为不变。
   */
  onActionTrack?: (action: 'preview' | 'download') => void
  /**
   * 「立即下载」实际下载动作的接管(可选)。传了 → 由调用方负责真正下载(如下载前扣积分);
   * 不传 → 走默认 downloadFile(行为不变)。仅在 onBeforeDownload 通过后才会被调用。
   */
  onDownload?: (file: FileItem) => void
}

/** 文件扩展名 → 卡片左侧图标 emoji */
function fileIcon(name: string): string {
  const ext = (name.split('.').pop() || '').toLowerCase()
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return '📊'
  if (ext === 'pdf') return '📕'
  if (ext === 'docx' || ext === 'doc') return '📄'
  return '📎'
}

/** 渲染单元格：纯文本直接显示，带 href 的渲染成可点击跳转的链接 */
function renderCell(cell: FilePreviewCell): React.ReactNode {
  if (typeof cell === 'string') return cell
  if ('type' in cell && cell.type === 'image') {
    return (
      <span className="aiClerk-filePreview__imageCell">
        <img
          className="aiClerk-filePreview__thumb"
          src={cell.src}
          alt={cell.text || '图片'}
          loading="lazy"
        />
      </span>
    )
  }
  if (!('href' in cell)) return cell.text || ''
  return (
    <a
      className="aiClerk-filePreview__link"
      href={cell.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {cell.text}
    </a>
  )
}

/** 内联预览表格：序号列 + 数据列；前 3 行序号高亮 */
const PreviewTable: React.FC<{
  columns: string[]
  rows: FilePreviewCell[][]
}> = ({ columns, rows }) => (
  <table className="aiClerk-filePreview__table">
    <thead>
      <tr>
        <th className="aiClerk-filePreview__idx">#</th>
        {columns.map((col, i) => (
          <th key={i}>{col}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, rowIdx) => (
        <tr key={rowIdx}>
          <td
            className={
              'aiClerk-filePreview__idx' +
              (rowIdx < 3 ? ' aiClerk-filePreview__idx--top' : '')
            }
          >
            {rowIdx + 1}
          </td>
          {columns.map((_, colIdx) => (
            <td key={colIdx}>{renderCell(row[colIdx] ?? '')}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
)

interface InlinePreviewState {
  loading: boolean
  error: string | null
  columns: string[]
  rows: FilePreviewCell[][]
}

const EMPTY_INLINE_PREVIEW: InlinePreviewState = {
  loading: false,
  error: null,
  columns: [],
  rows: []
}

async function buildInlinePreviewFromUrl(
  previewUrl: string
): Promise<Pick<InlinePreviewState, 'columns' | 'rows'>> {
  const rawBuffer = await loadFileBuffer(previewUrl)
  const buffer = await normalizeDrawingNamespaces(rawBuffer)
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(buffer)
  const sheet = workbook.worksheets[0]
  if (!sheet) return { columns: [], rows: [] }

  const cellImages = await parseCellImages(buffer)
  const floatingImages = buildFloatingImageMap(sheet, workbook)
  const colCount = Math.max(sheet.columnCount || 0, 1)
  const rowCount = sheet.rowCount || 0

  const columns: string[] = []
  for (let c = 1; c <= colCount; c++) {
    const text = cellDisplayText(sheet.getCell(1, c))
    columns.push(text || `列${c}`)
  }

  const rows: FilePreviewCell[][] = []
  for (let r = 2; r <= rowCount; r++) {
    const line: FilePreviewCell[] = []
    for (let c = 1; c <= colCount; c++) {
      line.push(
        cellPreviewValue(sheet.getCell(r, c), floatingImages, cellImages, r, c)
      )
    }
    rows.push(line)
  }

  return { columns, rows }
}

function buildFloatingImageMap(
  sheet: ExcelJS.Worksheet,
  workbook: ExcelJS.Workbook
): Map<string, string> {
  const images = new Map<string, string>()
  try {
    ;((sheet as any).getImages?.() || []).forEach((imgInfo: any) => {
      const tl = imgInfo?.range?.tl
      if (!tl) return
      const row =
        typeof tl.nativeRow === 'number'
          ? tl.nativeRow + 1
          : Math.floor(tl.row || 0) + 1
      const col =
        typeof tl.nativeCol === 'number'
          ? tl.nativeCol + 1
          : Math.floor(tl.col || 0) + 1
      const media = (workbook as any).getImage?.(imgInfo.imageId)
      const src = mediaToDataUrl(media)
      if (!src) return
      const key = `${row}-${col}`
      if (!images.has(key)) images.set(key, src)
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[file-preview] parse floating images failed:', e)
  }
  return images
}

function cellPreviewValue(
  cell: ExcelJS.Cell,
  floatingImages: Map<string, string>,
  cellImages: Map<string, string>,
  row: number,
  col: number
): FilePreviewCell {
  const floatingSrc = floatingImages.get(`${row}-${col}`)
  if (floatingSrc) return { type: 'image', src: floatingSrc, text: '图片' }

  const imageUrl = tryExtractImageFormulaUrl(cell)
  if (imageUrl) return { type: 'image', src: imageUrl, text: '图片' }

  const dispImgId = tryExtractDispImgId(cell)
  if (dispImgId) {
    const src = cellImages.get(dispImgId)
    if (src) return { type: 'image', src, text: '图片' }
  }

  const text = cellDisplayText(cell)
  const href = cellHyperlink(cell)
  if (href && text) return { text, href }
  return text
}

function cellDisplayText(cell: ExcelJS.Cell): string {
  const v: any = cell.value
  if (v == null) return ''
  if (typeof v === 'object' && Array.isArray(v.richText)) {
    return v.richText.map((rt: any) => String(rt.text || '')).join('')
  }
  if (typeof v === 'object' && 'text' in v) return String(v.text || '')
  if (typeof v === 'object' && 'result' in v) return String(v.result ?? '')
  if (v instanceof Date) return v.toLocaleDateString()
  return cell.text || String(v)
}

function cellHyperlink(cell: ExcelJS.Cell): string {
  const v: any = cell.value
  if (v && typeof v === 'object' && typeof v.hyperlink === 'string') {
    return v.hyperlink
  }
  return ''
}

function mediaToDataUrl(media: any): string {
  if (!media || !media.buffer) return ''
  const ext = String(media.extension || 'png').toLowerCase()
  const mime =
    ext === 'jpg' || ext === 'jpeg'
      ? 'image/jpeg'
      : ext === 'gif'
        ? 'image/gif'
        : ext === 'bmp'
          ? 'image/bmp'
          : ext === 'webp'
            ? 'image/webp'
            : 'image/png'
  return `data:${mime};base64,${bufferToBase64(media.buffer)}`
}

function bufferToBase64(buf: ArrayBuffer | Uint8Array | any): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf)
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + chunkSize)) as any
    )
  }
  return window.btoa(binary)
}

async function parseCellImages(
  buffer: ArrayBuffer
): Promise<Map<string, string>> {
  const result = new Map<string, string>()
  try {
    const zip = await JSZip.loadAsync(buffer)
    const cellImagesXml = zip.file('xl/cellimages.xml')
    const cellImagesRels = zip.file('xl/_rels/cellimages.xml.rels')
    if (!cellImagesXml || !cellImagesRels) return result

    const relsXml = await cellImagesRels.async('text')
    const relsDoc = new DOMParser().parseFromString(relsXml, 'application/xml')
    const rIdToTarget = new Map<string, string>()
    Array.from(relsDoc.getElementsByTagName('Relationship')).forEach((rel) => {
      const id = rel.getAttribute('Id')
      const target = rel.getAttribute('Target')
      if (id && target) rIdToTarget.set(id, target)
    })

    const xmlStr = await cellImagesXml.async('text')
    const doc = new DOMParser().parseFromString(xmlStr, 'application/xml')
    const REL_NS =
      'http://schemas.openxmlformats.org/officeDocument/2006/relationships'
    const mediaCache = new Map<string, string>()
    const nodes = Array.from(doc.getElementsByTagNameNS('*', 'cellImage'))
    for (const node of nodes) {
      const cNvPr = node.getElementsByTagNameNS(DRAWING_NS, 'cNvPr')[0]
      const imageId = cNvPr?.getAttribute('name')
      if (!imageId) continue
      const blip = node.getElementsByTagNameNS(MAIN_NS, 'blip')[0]
      const rId = blip?.getAttributeNS(REL_NS, 'embed')
      if (!rId) continue
      const target = rIdToTarget.get(rId)
      if (!target) continue
      const mediaPath = target.startsWith('/')
        ? target.slice(1)
        : target.startsWith('../')
          ? `xl/${target.slice(3)}`
          : `xl/${target}`
      let dataUrl = mediaCache.get(mediaPath)
      if (!dataUrl) {
        const mediaFile = zip.file(mediaPath)
        if (!mediaFile) continue
        const arrBuf = await mediaFile.async('arraybuffer')
        dataUrl = mediaPathToDataUrl(mediaPath, arrBuf)
        mediaCache.set(mediaPath, dataUrl)
      }
      result.set(imageId, dataUrl)
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[file-preview] parse cell images failed:', e)
  }
  return result
}

function mediaPathToDataUrl(mediaPath: string, buffer: ArrayBuffer): string {
  const ext = (mediaPath.split('.').pop() || 'png').toLowerCase()
  const mime =
    ext === 'jpg' || ext === 'jpeg'
      ? 'image/jpeg'
      : ext === 'gif'
        ? 'image/gif'
        : ext === 'bmp'
          ? 'image/bmp'
          : ext === 'webp'
            ? 'image/webp'
            : 'image/png'
  return `data:${mime};base64,${bufferToBase64(buffer)}`
}

function tryExtractImageFormulaUrl(cell: ExcelJS.Cell): string | null {
  const formula = cellFormula(cell)
  if (!formula) return null
  const match = IMAGE_FORMULA_RE.exec(formula)
  return match ? match[1] : null
}

function tryExtractDispImgId(cell: ExcelJS.Cell): string | null {
  const formula = cellFormula(cell)
  if (!formula) return null
  const match = DISPIMG_FORMULA_RE.exec(formula)
  return match ? match[1] : null
}

function cellFormula(cell: ExcelJS.Cell): string {
  const v: any = cell.value
  return (
    (v &&
      typeof v === 'object' &&
      typeof v.formula === 'string' &&
      v.formula) ||
    (typeof (cell as any).formula === 'string' ? (cell as any).formula : '')
  )
}

async function normalizeDrawingNamespaces(
  buffer: ArrayBuffer
): Promise<ArrayBuffer> {
  try {
    const zip = await JSZip.loadAsync(buffer)
    const REL_NS =
      'http://schemas.openxmlformats.org/officeDocument/2006/relationships'
    let touched = false
    const drawingPaths: string[] = []
    zip.forEach((path) => {
      if (/^xl\/drawings\/drawing\d+\.xml$/.test(path)) drawingPaths.push(path)
    })
    for (const path of drawingPaths) {
      let xml = await zip.file(path)!.async('text')
      const rootMatch = /<xdr:wsDr\b([^>]*)>/.exec(xml)
      if (rootMatch && !/\bxmlns:r\s*=/.test(rootMatch[1])) {
        xml = xml.replace(
          rootMatch[0],
          `<xdr:wsDr${rootMatch[1]} xmlns:r="${REL_NS}">`
        )
        zip.file(path, xml)
        touched = true
      }
    }
    if (!touched) return buffer
    return await zip.generateAsync({ type: 'arraybuffer' })
  } catch {
    return buffer
  }
}

export const FilePreviewCard: React.FC<FilePreviewCardProps> = ({
  file,
  fitPreviewToWindow,
  downloadLocked,
  onBeforeDownload,
  onActionTrack,
  onDownload
}) => {
  const [previewOpen, setPreviewOpen] = useState(false)
  const [inlinePreview, setInlinePreview] =
    useState<InlinePreviewState>(EMPTY_INLINE_PREVIEW)

  useEffect(() => {
    if (!file.preview_url) {
      setInlinePreview(EMPTY_INLINE_PREVIEW)
      return
    }
    let disposed = false
    setInlinePreview({
      loading: true,
      error: null,
      columns: [],
      rows: []
    })
    buildInlinePreviewFromUrl(file.preview_url)
      .then((inline) => {
        if (disposed) return
        setInlinePreview({
          loading: false,
          error: null,
          columns: inline.columns,
          rows: inline.rows
        })
      })
      .catch((err) => {
        if (disposed) return
        // eslint-disable-next-line no-console
        console.warn('[file-preview] load inline preview failed:', err)
        setInlinePreview({
          loading: false,
          error: '预览加载失败',
          columns: [],
          rows: []
        })
      })
    return () => {
      disposed = true
    }
  }, [file.preview_url])

  const handleOpenPreview = useCallback(() => {
    // xlsx 是表格:预览对所有人开放(看摘要引流),不拦截;收费卡在「下载」和全屏内卡点
    setPreviewOpen(true)
  }, [])
  const handleClosePreview = useCallback(() => setPreviewOpen(false), [])
  const handleDownload = useCallback(() => {
    if (onBeforeDownload && !onBeforeDownload(file)) return
    if (onDownload) {
      onDownload(file)
      return
    }
    downloadFile(file.url, file.name)
  }, [file, onBeforeDownload, onDownload])
  // 「会员下载」按钮:走 onBeforeDownload,非会员跳会员页(返回 false)
  const handleGoVip = useCallback(() => {
    if (onBeforeDownload) onBeforeDownload(file)
  }, [file, onBeforeDownload])

  // 列宽/行高:不再"铺满窗口"(列少会被撑超宽、单元格很空),改用「内容自然宽度 + 下限」:
  // 列宽 = max(原始列宽, minColWidthPx),行高 = max(原始行高, minRowHeightPx)。
  // 列少时表格按内容宽度居中、不撑满全屏;配合 zoom=1 字体也是正常大小。
  // 仅 fitPreviewToWindow=true(AI 问照片)启用;aiClerk 不传 → 原始尺寸(行为不变)。
  const previewColSizing = useMemo(
    () =>
      fitPreviewToWindow
        ? {
            minColWidthPx: 132,
            minRowHeightPx: 38,
            // 浮动图(如拍照台账的照片)收进单元格,避免跨列溢出
            containImages: true
          }
        : undefined,
    [fitPreviewToWindow]
  )

  const stats = [
    file.total_rows != null ? `${file.total_rows} 条记录` : null,
    file.total_cols != null ? `${file.total_cols} 列` : null,
    file.size ? `预计 ${file.size}` : null
  ]
    .filter(Boolean)
    .join(' · ')

  const modalBodyStyle: React.CSSProperties = fitPreviewToWindow
    ? {
        height: '80vh',
        maxHeight: '80vh',
        overflow: 'hidden',
        padding: 0,
        display: 'flex',
        flexDirection: 'column'
      }
    : {
        maxHeight: '80vh',
        overflow: 'auto',
        padding: 0
      }

  return (
    <div
      className={`aiClerk-filePreview${
        onBeforeDownload ? ' aiClerk-filePreview--blue' : ''
      }`}
    >
      <div className="aiClerk-filePreview__head">
        <span className="aiClerk-filePreview__icon">{fileIcon(file.name)}</span>
        <span className="aiClerk-filePreview__name" title={file.name}>
          {file.name}
        </span>
        <span className="aiClerk-filePreview__tag">预览模式</span>
      </div>

      {stats && <div className="aiClerk-filePreview__stats">{stats}</div>}

      {file.preview_url && (
        <div className="aiClerk-filePreview__tableWrap">
          {inlinePreview.loading ? (
            <div className="aiClerk-filePreview__state">预览加载中...</div>
          ) : inlinePreview.error ? (
            <div className="aiClerk-filePreview__state">
              {inlinePreview.error}
            </div>
          ) : inlinePreview.columns.length > 0 ? (
            <PreviewTable
              columns={inlinePreview.columns}
              rows={inlinePreview.rows}
            />
          ) : (
            <div className="aiClerk-filePreview__state">暂无预览内容</div>
          )}
        </div>
      )}

      <div className="aiClerk-filePreview__actions">
        {file.preview_url && (
          <button
            type="button"
            className="aiClerk-filePreview__btn aiClerk-filePreview__btn--ghost"
            style={onBeforeDownload ? { flex: 1 } : undefined}
            onClick={() => {
              if (onActionTrack) onActionTrack('preview')
              handleOpenPreview()
            }}
          >
            预览
          </button>
        )}
        {/* 下载:会员「立即下载」(蓝底无箭头);非会员「立即下载 👑」点击跳会员页 */}
        <button
          type="button"
          className="aiClerk-filePreview__btn aiClerk-filePreview__btn--primary"
          style={onBeforeDownload ? { flex: 1 } : undefined}
          onClick={() => {
            if (onActionTrack) onActionTrack('download')
            if (downloadLocked) handleGoVip()
            else handleDownload()
          }}
        >
          {downloadLocked ? (
            <>
              立即下载 <IconVipCrown />
            </>
          ) : (
            '立即下载'
          )}
        </button>
      </div>

      <Modal
        open={previewOpen}
        footer={null}
        width="90%"
        title={file.name}
        onCancel={handleClosePreview}
        styles={{ body: modalBodyStyle }}
        destroyOnHidden
      >
        {file.truncated && (
          <div className="aiClerk-filePreview__banner">
            仅预览部分数据（共 {file.total_rows ?? '—'} 条 ·{' '}
            {file.total_cols ?? '—'} 列），完整内容请下载
          </div>
        )}
        {downloadLocked && (
          // 非会员全屏预览:顶部商业化卡点,点击跳会员页
          <div
            role="button"
            onClick={handleGoVip}
            style={{
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '12px 16px',
              background: '#0070f4',
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            <IconVipCrown />
            开通会员，下载完整表格数据 →
          </div>
        )}
        {previewOpen && file.preview_url && (
          <DocumentPreview
            source={file.preview_url}
            colSizing={previewColSizing}
            zoom={fitPreviewToWindow ? 1 : PREVIEW_ZOOM}
          />
        )}
      </Modal>
    </div>
  )
}
